import {
  User,
  Users,
  Briefcase,
  MessageSquare,
  Bell,
  MessagesSquare,
  Bookmark,
  Link2,
  type LucideIcon,
} from "lucide-react";

// Launch-date constant. Drives the quiet per-card "New" tag (capabilities whose
// `since` is on or after launch) and the onboarding modal copy variant
// (profiles.createdAt on or after launch reads as newly verified).
export const LAUNCH_DATE = "2026-07-01";

export interface Capability {
  id: string;
  icon: LucideIcon;
  title: string;
  desc: string;
  // ISO date the capability shipped. Compared lexicographically against
  // LAUNCH_DATE to decide the "New" tag; historical values use YYYY-MM form.
  since: string;
  // Member-facing destination, when one exists. Omitted when the capability has
  // no standalone surface to link to.
  link?: string;
  // Excluded from the onboarding modal grid: the modal's permission callout
  // already carries invite at full weight.
  excludeFromModal?: boolean;
}

// The canonical capability set. Order is intentional and shared across the
// onboarding modal and the What's Here grid.
export const CAPABILITIES: Capability[] = [
  {
    id: "your-profile",
    icon: User,
    title: "Your Profile",
    desc: "Upload a custom profile pic, or sync a new one from your service of choice. Use modular blocks to build your profile as you prefer.",
    since: "2026-07-09",
    link: "/profile/edit",
  },
  {
    id: "directory",
    icon: Users,
    title: "Directory",
    desc: "Find and reconnect with verified Riot alumni by discipline, location, and availability.",
    since: "2025-01",
    link: "/?tab=talent",
  },
  {
    id: "opportunities",
    icon: Briefcase,
    title: "Opportunities",
    desc: "Openings from hiring teams, plus referrals and leads shared by fellow alumni.",
    since: "2025-01",
    link: "/?tab=opportunities",
  },
  {
    id: "community",
    icon: MessageSquare,
    title: "Community",
    desc: "Events, communities, and projects from fellow alumni.",
    since: "2025-01",
    link: "/communities-events",
  },
  {
    id: "job-alerts",
    icon: Bell,
    title: "Job Alerts",
    desc: "Choose disciplines and titles, and get an email when a matching opportunity is posted.",
    since: "2025-01",
    link: "/?tab=opportunities",
  },
  {
    id: "community-post-alerts",
    icon: MessagesSquare,
    title: "Community Post Alerts",
    desc: "Get notified when new community posts match the tags or post types you follow.",
    since: "2025-01",
    link: "/communities-events",
  },
  {
    id: "saved-profiles",
    icon: Bookmark,
    title: "Saved Profiles",
    desc: "Bookmark alumni you want to keep track of.",
    since: "2025-01",
    link: "/saved",
  },
  {
    id: "invite-alumni",
    icon: Link2,
    title: "Invite Alumni",
    desc: "Bring fellow Riot alumni into the network with your personal invite link.",
    since: "2026-07-09",
    link: "/dashboard#invite",
    excludeFromModal: true,
  },
];

// Cards shown inside the onboarding modal: the full set minus Invite Alumni.
export const MODAL_CAPABILITIES: Capability[] = CAPABILITIES.filter(
  (c) => !c.excludeFromModal,
);

// A capability is "New" when it shipped on or after launch.
export function isNewCapability(since: string): boolean {
  return since >= LAUNCH_DATE;
}

// Whether a capability reads as "New" for a specific member, based on when they
// last viewed the Features page. A member who has never viewed it (null) is
// treated as having last looked at launch, so only post-launch capabilities read
// as new — never the entire pre-launch catalogue. `featuresLastViewedAt` is a
// full ISO timestamp; `since` is YYYY-MM-DD or YYYY-MM, so compare on the date.
export function capabilityIsNewForUser(
  since: string,
  featuresLastViewedAt: string | null | undefined,
): boolean {
  const baseline = featuresLastViewedAt ? featuresLastViewedAt.slice(0, 10) : LAUNCH_DATE;
  return since > baseline;
}

// Whether any capability is new for this member — drives the nav notification dot.
export function hasNewCapabilitiesForUser(
  featuresLastViewedAt: string | null | undefined,
): boolean {
  return CAPABILITIES.some((c) => capabilityIsNewForUser(c.since, featuresLastViewedAt));
}

// A member is newly verified when their profile was created on or after launch.
// Both values are ISO strings that start with YYYY-MM-DD, so a lexicographic
// comparison is correct. Veterans (created before launch) must never see the
// welcome variant.
export function isNewlyVerified(createdAt: string | null | undefined): boolean {
  if (!createdAt) return false;
  return createdAt >= LAUNCH_DATE;
}
